import { useLayoutEffect, useRef } from 'react';
import { AppBar, Layout, Sidebar, TitlePortal, useSidebarState, type AppBarProps, type LayoutProps, type SidebarProps } from 'react-admin';
import { Box, Typography, useMediaQuery, type Theme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { CustomMenu } from './CustomMenu';
import { adminBrand, navigationForPath, overview } from '../config/navigation';
import { useAdminText } from './shared/useAdminText';

function WorkspaceBar(props: AppBarProps) {
  return <AppBar {...props} color="default" elevation={0}>
    <Typography variant="body2" fontWeight={700} sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>{adminBrand.name}</Typography>
    <TitlePortal sx={{ flex: 1, fontSize: '0.875rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
  </AppBar>;
}

function PageIdentity() {
  const { pathname } = useLocation();
  const t = useAdminText();
  const match = navigationForPath(pathname);
  if (!match && pathname !== '/') return null;
  const item = match ?? overview;
  return <Box component="header" sx={{ pt: { xs: 2.5, md: 3.5 }, pb: 2.5 }}>
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.1em', fontSize: 10.5, fontWeight: 700 }}>
      {match ? t(`groups.${match.group.id}`, match.group.label) : t('workspace', adminBrand.workspace)}
    </Typography>
    <Typography component="h1" variant="h4" sx={{ mt: 0.5, mb: 0.75 }}>{t(`navigation.${item.id}`, item.label)}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>{t(`description.${item.id}`, item.description)}</Typography>
  </Box>;
}

function WorkspaceSidebar(props: SidebarProps) {
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [open, setOpen] = useSidebarState();
  const previouslyMobile = useRef(false);
  const enteringMobile = mobile && !previouslyMobile.current;
  // Do not briefly mount an open modal when switching from the desktop rail.
  // Mounting then closing it in the same frame can leave the main content aria-hidden.
  useLayoutEffect(() => {
    previouslyMobile.current = mobile;
    if (mobile) setOpen(false);
  }, [mobile, setOpen]);
  return <Sidebar {...props} open={enteringMobile ? false : open} />;
}

export function CustomLayout({ children, ...props }: LayoutProps) {
  return <Layout {...props} menu={CustomMenu} appBar={WorkspaceBar} sidebar={WorkspaceSidebar} appBarAlwaysOn>
    <PageIdentity />{children}
  </Layout>;
}
